import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader } from "../components/layout/Shell";
import { ResponsivePageContainer } from "../components/shared/ResponsivePageContainer";
import PayNowModal from "../components/payment/PayNowModal";

const STATUS_META = {
  DRAFT:         { label: "Draft",         color: "bg-slate-500/15 text-slate-400  border-slate-500/30" },
  SUBMITTED:     { label: "Submitted",     color: "bg-blue-500/15   text-blue-400   border-blue-500/30" },
  UNDER_REVIEW:  { label: "Under Review",  color: "bg-amber-500/15  text-amber-400  border-amber-500/30" },
  DECISION_MADE: { label: "Decision Made", color: "bg-violet-500/15 text-violet-400 border-violet-500/30" },
  WITHDRAWN:     { label: "Withdrawn",     color: "bg-slate-500/15  text-slate-400  border-slate-500/30" },
  CANCELLED:     { label: "Cancelled",     color: "bg-red-500/15    text-red-400    border-red-500/30" },
};

const DECISION_META = {
  ADMIT:    { label: "Admitted",    color: "text-emerald-400" },
  REJECT:   { label: "Rejected",    color: "text-red-400"     },
  WAITLIST: { label: "Waitlisted",  color: "text-amber-400"   },
  DEFER:    { label: "Deferred",    color: "text-blue-400"    },
};

const OFFER_META = {
  ISSUED:   { label: "Offer Issued",    color: "text-amber-400"   },
  ACCEPTED: { label: "Offer Accepted",  color: "text-emerald-400" },
  DECLINED: { label: "Offer Declined",  color: "text-red-400"     },
  EXPIRED:  { label: "Offer Expired",   color: "text-slate-400"   },
  REVOKED:  { label: "Offer Revoked",   color: "text-slate-400"   },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status, color: "bg-slate-500/15 text-slate-400 border-slate-500/30" };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-medium ${m.color}`}>
      {m.label}
    </span>
  );
}

function DecisionCell({ decision }) {
  if (!decision) return <span className="text-muted-foreground text-xs">—</span>;
  const m = DECISION_META[decision] || { label: decision, color: "text-slate-400" };
  return <span className={`text-xs font-medium ${m.color}`}>{m.label}</span>;
}

function OfferCell({ offerStatus }) {
  if (!offerStatus) return <span className="text-muted-foreground text-xs">—</span>;
  const m = OFFER_META[offerStatus] || { label: offerStatus, color: "text-slate-400" };
  return <span className={`text-xs font-medium ${m.color}`}>{m.label}</span>;
}

function LegacyCRMSection() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const r = await api.get("/v1/enroll/leads?limit=5");
      const raw = r.data?.leads ?? r.data;
      setLeads(Array.isArray(raw) ? raw : []);
    } catch {
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [open]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="mt-8 border border-dashed border-amber-500/30 rounded-xl p-5"
         data-testid="legacy-admissions-section">
      <div className="flex items-center gap-3 mb-2">
        <span className="px-2 py-0.5 text-xs font-semibold tracking-widest bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded">
          TRANSITIONAL
        </span>
        <span className="text-sm font-medium text-muted-foreground">Legacy CRM / Recruitment Leads</span>
        <span className="ml-auto text-xs text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
              onClick={() => setOpen(v => !v)}
              data-testid="legacy-admissions-toggle">
          {open ? "Hide" : "Show legacy data"}
        </span>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        MongoDB CRM lead records. Not co-canonical. This section will be retired when the Recruitment &amp; Admissions
        pipeline is fully migrated to canonical PostgreSQL.
      </p>
      {open && (
        loading
          ? <p className="text-xs text-muted-foreground">Loading legacy records…</p>
          : leads.length === 0
            ? <p className="text-xs text-muted-foreground">No legacy lead records found.</p>
            : <div className="space-y-1">
                {leads.map(l => (
                  <div key={l._id || l.id} className="flex items-center gap-3 text-xs text-muted-foreground py-1 border-b border-border/30 last:border-0">
                    <span>{l.name || l.applicant_name || "—"}</span>
                    <span className="ml-auto">{l.status || l.stage || "—"}</span>
                  </div>
                ))}
              </div>
      )}
    </div>
  );
}

export default function AdmissionsApplications() {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [cycleFilter, setCycleFilter] = useState("");
  const [offset, setOffset] = useState(0);
  const LIMIT = 20;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: LIMIT, offset });
      if (statusFilter) params.set("status", statusFilter);
      if (cycleFilter)  params.set("cycle_code", cycleFilter);
      const r = await api.get(`/core/applications?${params}`);
      setApps(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load applications.");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, cycleFilter, offset]);

  useEffect(() => { load(); }, [load]);

  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setOffset(0);
  };

  return (
    <div data-testid="admissions-applications-page">
      <PageHeader
        eyebrow="Recruitment & Admissions"
        title="Applications"
        description="Canonical application pipeline — PostgreSQL-backed identity and lifecycle."
        actions={
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-1 text-xs font-semibold tracking-widest bg-emerald-500/10 text-emerald-700 border border-emerald-500/20 rounded">
              CANONICAL PG
            </span>
            {!loading && (
              <span className="text-sm text-muted-foreground" data-testid="applications-total-count">
                {total} application{total !== 1 ? "s" : ""}
              </span>
            )}
          </div>
        }
      />
      <ResponsivePageContainer>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          <select
            className="bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            value={statusFilter}
            onChange={handleFilterChange(setStatusFilter)}
            data-testid="applications-status-filter"
          >
            <option value="">All statuses</option>
            <option value="DRAFT">Draft</option>
            <option value="SUBMITTED">Submitted</option>
            <option value="UNDER_REVIEW">Under Review</option>
            <option value="DECISION_MADE">Decision Made</option>
            <option value="WITHDRAWN">Withdrawn</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
          <input
            type="text"
            placeholder="Search by intake cycle…"
            className="bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring min-w-0 flex-1 sm:flex-none"
            value={cycleFilter}
            onChange={handleFilterChange(setCycleFilter)}
            data-testid="applications-cycle-search"
          />
          <button
            className="px-3 py-2 text-xs border border-border rounded-lg text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors whitespace-nowrap"
            onClick={() => { setStatusFilter(""); setCycleFilter(""); setOffset(0); }}
            data-testid="applications-clear-filters"
          >
            Clear
          </button>
        </div>

        {/* Table */}
        <div className="border border-border rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground text-sm"
                 data-testid="applications-loading">
              Loading applications…
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-16 text-destructive text-sm"
                 data-testid="applications-error">
              {error}
            </div>
          ) : apps.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground"
                 data-testid="applications-empty">
              <p className="text-sm">No applications found.</p>
              <p className="text-xs mt-1">Try clearing the filters or running the demo seed.</p>
            </div>
          ) : (
            <div className="table-scroll-container">
              <table className="w-full text-sm" data-testid="canonical-applications-table">
                <thead>
                  <tr className="border-b border-border bg-muted/30 text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-3 text-left">Applicant</th>
                    <th className="px-4 py-3 text-left">Program</th>
                    <th className="px-4 py-3 text-left hidden sm:table-cell">Cycle</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-left hidden md:table-cell">Decision</th>
                    <th className="px-4 py-3 text-left hidden md:table-cell">Offer</th>
                    <th className="px-4 py-3 text-left hidden lg:table-cell">Submitted</th>
                    <th className="px-4 py-3 text-left hidden lg:table-cell">Source</th>
                    <th className="px-4 py-3 text-left">Fee</th>
                  </tr>
                </thead>
                <tbody>
                  {apps.map((app) => (
                    <tr
                      key={app.id}
                      className="border-b border-border/50 hover:bg-muted/20 cursor-pointer transition-colors"
                      onClick={() => navigate(`/admissions/applications/${app.id}`)}
                      data-testid={`application-row-${app.id}`}
                    >
                      <td className="px-4 py-3">
                        <span className="font-medium text-foreground">
                          {app.applicant?.display_name || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground text-xs max-w-[140px] truncate">
                        {app.program?.title || (app.program_id ? app.program_id.slice(0,8)+"…" : "—")}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">{app.cycle_code || "—"}</td>
                      <td className="px-4 py-3"><StatusBadge status={app.status} /></td>
                      <td className="px-4 py-3 hidden md:table-cell"><DecisionCell decision={app.current_decision} /></td>
                      <td className="px-4 py-3 hidden md:table-cell"><OfferCell offerStatus={app.current_offer_status} /></td>
                      <td className="px-4 py-3 text-muted-foreground text-xs hidden lg:table-cell">
                        {app.submitted_at ? new Date(app.submitted_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className="text-xs text-emerald-600 font-mono">CANONICAL PG</span>
                      </td>
                      {/* PAY-UNIFIED-01: Application Fee payment */}
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        {app.fee_payment_status === "PAID" ? (
                          <span className="text-xs font-semibold text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded px-2 py-0.5">
                            Paid
                          </span>
                        ) : ["SUBMITTED", "UNDER_REVIEW", "DRAFT"].includes(app.status) ? (
                          <PayNowModal
                            context="ADMISSIONS"
                            contextId={app.id}
                            amount="500"
                            currency="INR"
                            label="Pay Fee"
                            applicantEmail={app.applicant?.email || "applicant@academiaos.ai"}
                            className="text-xs py-1 px-2.5"
                          />
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {!loading && !error && total > LIMIT && (
          <div className="flex flex-col gap-2 mt-4 sm:flex-row sm:items-center sm:justify-between text-sm text-muted-foreground">
            <span>Showing {offset + 1}–{Math.min(offset + LIMIT, total)} of {total}</span>
            <div className="flex gap-2">
              <button
                className="px-3 py-1.5 border border-border rounded-lg disabled:opacity-40 hover:bg-muted/30 transition-colors"
                disabled={offset === 0}
                onClick={() => setOffset(o => Math.max(0, o - LIMIT))}
                data-testid="applications-prev-page"
              >Previous</button>
              <button
                className="px-3 py-1.5 border border-border rounded-lg disabled:opacity-40 hover:bg-muted/30 transition-colors"
                disabled={offset + LIMIT >= total}
                onClick={() => setOffset(o => o + LIMIT)}
                data-testid="applications-next-page"
              >Next</button>
            </div>
          </div>
        )}

        {/* Legacy CRM */}
        <LegacyCRMSection />
      </ResponsivePageContainer>
    </div>
  );
}
